import { useEffect, useState } from "react";
import {
  X,
  Megaphone,
  Wrench,
  AlertTriangle,
  Sparkles,
  Info,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
} from "lucide-react";
import { getBannerAnnouncements, ClienteAnnouncement } from "services/comunicados/comunicados.service";

const DISMISSED_KEY = "INTREX_DISMISSED_ANNOUNCEMENTS";

const getDismissed = (): number[] => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]");
  } catch {
    return [];
  }
};

type CategoryStyle = {
  icon: LucideIcon;
  accent: string;
  iconBg: string;
  iconText: string;
  label: string;
};

const CATEGORY_STYLE: Record<string, CategoryStyle> = {
  atualizacao: { icon: Sparkles, accent: "bg-brand-primary", iconBg: "bg-brand-primary/10", iconText: "text-brand-primary", label: "Atualização" },
  manutencao: { icon: Wrench, accent: "bg-amber-500", iconBg: "bg-amber-50", iconText: "text-amber-600", label: "Manutenção" },
  incidente: { icon: AlertTriangle, accent: "bg-red-500", iconBg: "bg-red-50", iconText: "text-red-600", label: "Incidente" },
  novidade: { icon: Megaphone, accent: "bg-emerald-500", iconBg: "bg-emerald-50", iconText: "text-emerald-600", label: "Novidade" },
  // "Novidade" é verde e comemorativo — errado para avisar que uma
  // transportadora passou a cobrar mais. Âmbar avisa sem alarmar, que é o tom
  // certo para tarifa, prazo e mudança de regra.
  aviso: { icon: Info, accent: "bg-amber-500", iconBg: "bg-amber-50", iconText: "text-amber-700", label: "Aviso" },
};

const DEFAULT_STYLE = CATEGORY_STYLE.atualizacao;

/**
 * "10 set" — curto, só para dar idade ao aviso sem roubar atenção.
 *
 * Montado à mão em vez de toLocaleDateString: o pt-BR devolve "10 de set.",
 * com o "de" e o ponto. E o fuso é fixado em São Paulo porque um horário
 * gravado à meia-noite UTC apareceria como o dia anterior.
 */
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const formatarData = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const partes = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
  }).formatToParts(d);

  const dia = partes.find((p) => p.type === "day")?.value;
  const mes = partes.find((p) => p.type === "month")?.value;
  if (!dia || !mes) return null;

  return `${dia} ${MESES[Number(mes) - 1] ?? ""}`.trim();
};

const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState<ClienteAnnouncement[]>([]);
  const [indice, setIndice] = useState(0);

  useEffect(() => {
    getBannerAnnouncements()
      .then((data) => {
        const dismissed = getDismissed();
        setAnnouncements(data.filter((a) => !dismissed.includes(a.id)));
      })
      .catch(() => setAnnouncements([]));
  }, []);

  const dismiss = (id: number) => {
    const dismissed = getDismissed();
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed, id]));
    setAnnouncements((prev) => {
      const restante = prev.filter((a) => a.id !== id);
      setIndice((i) => Math.min(i, Math.max(0, restante.length - 1)));
      return restante;
    });
  };

  if (!announcements.length) return null;

  const current = announcements[indice] ?? announcements[0];
  const style = CATEGORY_STYLE[current.category] ?? DEFAULT_STYLE;
  const Icon = style.icon;
  const data = formatarData(current.publishedAt);
  const total = announcements.length;

  return (
    <div className="relative flex items-start gap-3 border-b border-slate-200 bg-white py-3 pl-5 pr-4">
      <span className={`absolute left-0 top-0 h-full w-1 ${style.accent}`} />

      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${style.iconBg}`}>
        <Icon className={`h-4 w-4 ${style.iconText}`} />
      </span>

      {/* max-w-3xl: sem isso a linha se estica pela largura toda da tela, e
          texto com 200 caracteres de largura é desconfortável de ler. */}
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-brand-sidebar">
            {current.title}
          </span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${style.iconBg} ${style.iconText}`}
          >
            {style.label}
          </span>
          {data && (
            // Aviso de tarifa sem data envelhece sem ninguém perceber.
            <span className="text-[11px] text-slate-400">{data}</span>
          )}
        </div>
        <p className="mt-0.5 max-w-3xl break-words text-sm leading-relaxed text-slate-600">
          {current.body}
        </p>
      </div>

      {/* Antes só o primeiro aviso aparecia e os outros ficavam invisíveis até
          este ser dispensado. Agora dá para navegar sem precisar descartar. */}
      {total > 1 && (
        <div className="flex shrink-0 items-center gap-1 pt-0.5 text-slate-400">
          <button
            type="button"
            aria-label="Aviso anterior"
            disabled={indice === 0}
            onClick={() => setIndice((i) => Math.max(0, i - 1))}
            className="rounded-full p-1 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-[11px] tabular-nums">
            {indice + 1}/{total}
          </span>
          <button
            type="button"
            aria-label="Próximo aviso"
            disabled={indice >= total - 1}
            onClick={() => setIndice((i) => Math.min(total - 1, i + 1))}
            className="rounded-full p-1 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <button
        onClick={() => dismiss(current.id)}
        aria-label="Fechar"
        className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default AnnouncementBanner;
